import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, Dimensions, Platform } from 'react-native';
import { BarChart } from 'react-native-gifted-charts';
import { useTheme, PoppinsFonts } from '../../theme';
import { axisUnit, compactAxisTick, formatWithCommas, fs, s, v } from '../../utils/libs';
import ChartTooltip from './ChartTooltip';
import PositionedChartTooltip from './PositionedChartTooltip';
import {
  estimateChartTooltipHeight,
  getBarChartPointX,
  getBarChartTooltipTranslateY,
  getBarHeightFromValue,
} from './chartTooltipPosition';

type AnnualPoint = {
  month: string;
  totalsByType: Record<string, number>;
  total: number;
};

type Props = {
  data: AnnualPoint[];
  typeColors?: Record<string, string>;
};

const AnnualExpensesBarChart: React.FC<Props> = ({ data, typeColors }) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [containerW, setContainerW] = useState<number | null>(null);
  const yAxisLabelW = s(Platform.OS === 'web' ? 56 : 40);
  const chartWidth = useMemo(() => {
    const screenWidth = Dimensions.get('window').width;
    const fallback = screenWidth - s(32);
    const measured = containerW
      ? containerW - yAxisLabelW - s(16)
      : fallback - yAxisLabelW;
    return Math.max(measured, s(140));
  }, [containerW, yAxisLabelW]);
  const initialSpacing = s(10);
  const pointerLabelW = s(150);
  const endSpacing = s(24);
  const plotHeight = v(200);

  const typeOrder = useMemo(() => {
    const totals: Record<string, number> = {};
    (data || []).forEach(p => {
      Object.entries(p?.totalsByType || {}).forEach(([k, val]) => {
        const key = String(k);
        const num = Number(val || 0);
        totals[key] = (totals[key] || 0) + num;
      });
    });
    return Object.keys(totals).sort(
      (a, b) => (totals[b] || 0) - (totals[a] || 0),
    );
  }, [data]);

  const orderedTypeColors = useMemo(() => {
    const fallbackPalette = [
      theme.colors.secondary,
      theme.colors.success,
      theme.colors.info,
      theme.colors.premium,
      theme.colors.primary,
      theme.colors.secondary,
    ];
    const map: Record<string, string> = {};
    typeOrder.forEach((t, idx) => {
      const provided = typeColors?.[t];
      map[t] = provided || fallbackPalette[idx % fallbackPalette.length];
    });
    return map;
  }, [typeOrder, typeColors, theme.colors]);

  const { barWidth, spacing } = useMemo(() => {
    const n = Math.max((data || []).length, 1);
    const spacingMin = s(Platform.OS === 'web' ? 20 : 8);
    const barMin = s(Platform.OS === 'web' ? 16 : 10);
    const barMax = s(Platform.OS === 'web' ? 28 : 16);
    const available = Math.max(chartWidth - initialSpacing - endSpacing, 1);
    let computedBar = Math.min(
      barMax,
      Math.max(barMin, (available - spacingMin * Math.max(n - 1, 0)) / n),
    );
    let computedSpacing = n > 1 ? (available - computedBar * n) / (n - 1) : 0;
    if (computedSpacing < spacingMin) {
      computedSpacing = Math.max(
        0,
        (available - computedBar * n) / Math.max(n - 1, 1),
      );
    }
    return { barWidth: computedBar, spacing: computedSpacing };
  }, [data, chartWidth, initialSpacing, endSpacing]);

  const maxY = useMemo(() => {
    const barTotals = (data || []).map(p =>
      Object.values(p?.totalsByType || {}).reduce(
        (acc, val) => acc + Number(val || 0),
        0,
      ),
    );
    const max = Math.max(0, ...barTotals);
    const rounded = Math.ceil(max / 10) * 10;
    return rounded === 0 ? 10 : rounded;
  }, [data]);

  const yUnit = useMemo(() => axisUnit(maxY), [maxY]);
  const shouldRotateX = useMemo(
    () => (data || []).some(d => String(d.month || '').length >= 5),
    [data],
  );

  const stackedData = useMemo(() => {
    return (data || []).map(point => {
      const stacks = (typeOrder || []).map(t => ({
        value: Number(point?.totalsByType?.[t] || 0),
        color: orderedTypeColors[t],
      }));
      return {
        label: point.month,
        stacks,
      } as any;
    });
  }, [data, typeOrder, orderedTypeColors]);

  const tooltipBoundaryWidth = chartWidth;
  const isEmpty = (stackedData?.length || 0) === 0;
  const tooltipHeight = estimateChartTooltipHeight(
    Math.max(typeOrder.length, 1),
  );

  const shouldRenderChart = useMemo(
    () => (data || []).length > 0 && (typeOrder || []).length > 0,
    [data, typeOrder],
  );

  return (
    <View
      style={styles.container}
      onLayout={e => setContainerW(e.nativeEvent.layout.width)}>
      {!shouldRenderChart ? (
        <Text style={styles.emptyText}>No data</Text>
      ) : (
        <BarChart
          stackData={stackedData}
          width={chartWidth}
          xAxisLength={chartWidth}
          rulesLength={chartWidth}
          endSpacing={endSpacing}
          initialSpacing={initialSpacing}
          spacing={spacing}
          barWidth={barWidth}
          disableScroll
          yAxisLabelWidth={yAxisLabelW}
          noOfSections={5}
          labelWidth={s(36)}
          xAxisLabelsHeight={30}
          maxValue={maxY}
          height={plotHeight}
          hideRules
          yAxisTextStyle={{ color: theme.colors.text, fontSize: fs(11) }}
          xAxisLabelTextStyle={{
            color: theme.colors.text,
            fontSize: fs(10),
            textAlign: 'center',
          }}
          isAnimated
          animationDuration={600}
          rotateLabel={shouldRotateX}
          labelsExtraHeight={shouldRotateX ? v(18) : 0}
          xAxisTextNumberOfLines={1}
          formatYLabel={label =>
            compactAxisTick(Math.round(Number(label || '0')), yUnit)
          }
          renderTooltip={
            isEmpty
              ? undefined
              : (item: any, index: number) => {
                  const safeIndex = Math.max(
                    0,
                    Math.min(index, Math.max(stackedData.length - 1, 0)),
                  );
                  const label = String(item?.label ?? '');
                  const stacks = (item?.stacks ?? []) as Array<{
                    value: number;
                    color: string;
                  }>;
                  const pointX = getBarChartPointX({
                    index: safeIndex,
                    initialSpacing,
                    barWidth,
                    spacing,
                  });
                  const stackTotal = stacks.reduce(
                    (sum, stack) => sum + Number(stack?.value ?? 0),
                    0,
                  );
                  const barHeight = getBarHeightFromValue(
                    stackTotal,
                    maxY,
                    plotHeight,
                  );
                  const translateY = getBarChartTooltipTranslateY({
                    barHeight,
                    plotHeight,
                    tooltipHeight,
                    gap: s(8),
                  });
                  const tooltipItems = (typeOrder || []).map((t, idx) => ({
                    color: orderedTypeColors[t],
                    label: t,
                    value: Number(stacks[idx]?.value || 0),
                    formatter: (n: number) =>
                      formatWithCommas(Math.round(n), 0),
                  }));

                  return (
                    <PositionedChartTooltip
                      pointX={pointX}
                      tooltipWidth={pointerLabelW}
                      boundaryWidth={tooltipBoundaryWidth}
                      translateY={translateY}>
                      <View style={{ width: pointerLabelW }}>
                        <ChartTooltip header={label} items={tooltipItems} />
                      </View>
                    </PositionedChartTooltip>
                  );
                }
          }
        />
      )}
    </View>
  );
};

export default AnnualExpensesBarChart;

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      padding: s(8),
    },
    emptyText: {
      color: theme.colors.textSecondary,
      fontFamily: PoppinsFonts.regular,
      fontSize: fs(12),
    },
  });
